import { CompletionArt } from '../ui/controls';
import { useReducer, useEffect, useCallback, useState, useRef, useMemo, type MouseEvent as ReactMouseEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
import { getDateSeed } from '../../lib/dateSeed';
import { RefreshCw, Trophy, Gauge } from 'lucide-react'; // Import Trophy and Gauge icons
import {
  formatDisplayDate,
  formatTimeDisplay,
  formatCompletionTime,
  createTimerTick,
  getIncompleteDailyGames,
  saveGameToDatabase,
  checkPersonalBest
} from '../../lib/gameUtils';
import { isDevelopment } from '../../lib/utils';
import { useSound } from '../../hooks/useSound';
import { toast } from 'sonner';
import {
  canRestorePuzzleState,
  createDailyPuzzleIdentity,
  formatDateString as formatPuzzleDateString,
  parseDateString,
  type DailyPuzzleGame,
  type DailyPuzzleIdentity
} from '../../lib/dailyPuzzle';
import { useDailyPuzzleRollover } from '../../hooks/useDailyPuzzleRollover';
// import { debug } from '../../lib/utils';

// Constants
const BOARD_SIZE = 4;
const TILE_COUNT = BOARD_SIZE * BOARD_SIZE - 1; // 15 tiles
const DEFAULT_GAME_STATS = {
  moves: 0,
  hintsUsed: 0,
  numbersUsed: false
};

const STORAGE_KEYS = {
  BOARD: 'fifteen-puzzle-board',
  STATS: 'fifteen-puzzle-stats',
  ELAPSED_TIME: 'fifteen-puzzle-elapsed-time',
  IS_COMPLETED: 'fifteen-puzzle-is-completed',
  MOVE_HISTORY: 'fifteen-puzzle-move-history',
  SOLUTION: 'fifteen-puzzle-solution',
  SOLUTION_STEP: 'fifteen-puzzle-solution-step',
  COMPLETION_TIME: 'fifteen-puzzle-completion-time',
  COMPLETED: 'fifteen-puzzle-completed',
  IDENTITY: 'fifteen-puzzle-identity'
};

// Utility functions

const getNextPuzzleNumber = (currentPuzzleNumber: number): number => {
  return currentPuzzleNumber === 9 ? 1 : currentPuzzleNumber + 1;
};

// Types
interface GameStats {
  moves: number;
  hintsUsed: number;
  numbersUsed: boolean;
}

interface BoardState {
  tiles: number[];
  emptyIndex: number;
}

// Game reducer types
type GameAction =
  | { type: 'INITIALIZE_BOARD', payload: { tiles: number[], emptyIndex: number } }
  | { type: 'MOVE_TILE', payload: { index: number, newEmptyIndex: number } }
  | { type: 'INCREMENT_MOVES' }
  | { type: 'UNDO_MOVE', payload: { previousBoard: BoardState } }
  | { type: 'SET_COMPLETED', payload: boolean }
  | { type: 'SET_HINT', payload: number | null }
  | { type: 'USE_HINT' }
  | { type: 'TOGGLE_PAUSE' }
  | { type: 'RESET_GAME', payload: GameStats }
  | { type: 'REQUEST_HINT', payload: boolean }
  | { type: 'SET_CALCULATING_SOLUTION', payload: boolean }
  | { type: 'SHOW_COMPLETION_DIALOG' }
  | { type: 'CLOSE_COMPLETION_DIALOG' }
  | { type: 'SHOW_CONFIRM_DIALOG' }
  | { type: 'CLOSE_CONFIRM_DIALOG' }
  | { type: 'COMPLETE_GAME' }
  | { type: 'WIN_GAME' }
  | { type: 'SET_DIFFICULTY', payload: number }
  | { type: 'UPDATE_STATS', payload: GameStats }; // New action for updating stats

interface GameState {
  board: BoardState;
  stats: GameStats;
  isCompleted: boolean;
  isPaused: boolean;
  hintedTile: number | null;
  hintRequested: boolean;
  calculatingSolution: boolean;
  showCompletionDialog: boolean;
  showConfirmDialog: boolean;
  difficultyLevel: number;
}

interface FifteenPuzzleGameProps {
  initialSeed?: number;
  puzzleDate?: Date | null;
  difficulty?: number | null;
  isPreviewMode?: boolean;
}

// Check if a tile is movable by checking if it's adjacent to the empty space
const isTileMovable = (index: number, emptyIndex: number, boardSize: number = BOARD_SIZE): boolean => {
  const emptyRow = Math.floor(emptyIndex / boardSize);
  const emptyCol = emptyIndex % boardSize;
  const tileRow = Math.floor(index / boardSize);
  const tileCol = index % boardSize;

  // Check if tile is adjacent to empty space (same row or column, one step away)
  return (
    (tileRow === emptyRow && Math.abs(tileCol - emptyCol) === 1) ||
    (tileCol === emptyCol && Math.abs(tileRow - emptyRow) === 1)
  );
};

// Check if the board is solvable
const isSolvable = (tiles: number[], emptyIndex: number): boolean => {
  // Count inversions
  let inversions = 0;
  for (let i = 0; i < tiles.length; i++) {
    if (tiles[i] === 0) continue;

    for (let j = i + 1; j < tiles.length; j++) {
      if (tiles[j] === 0) continue;
      if (tiles[i] > tiles[j]) {
        inversions++;
      }
    }
  }

  // For 4x4 board:
  // - If empty tile is on an even row from the bottom (0-indexed), the number of inversions must be odd
  // - If empty tile is on an odd row from the bottom (0-indexed), the number of inversions must be even
  const emptyRow = Math.floor(emptyIndex / BOARD_SIZE);
  const rowFromBottom = BOARD_SIZE - 1 - emptyRow;

  // Fix: Correct solvability condition
  return (rowFromBottom % 2 === 0) ? (inversions % 2 === 0) : (inversions % 2 === 1);
};

// Generate a solvable puzzle board
const generateSolvableBoard = (seed: number): BoardState => {
  // Create a simple deterministic random number generator
  const createRNG = (seed: number) => {
    let state = seed;
    return () => {
      // Simple LCG (Linear Congruential Generator)
      state = (state * 1664525 + 1013904223) % 2**32;
      return state / 2**32; // Return a value between 0 and 1
    };
  };

  const rng = createRNG(seed);

  // Create a solved board first (1-15, then 0 for empty)
  const tiles = Array.from({ length: TILE_COUNT }, (_, i) => i + 1);
  tiles[TILE_COUNT] = 0; // Add empty tile

  // Fisher-Yates shuffle with our seeded random
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
  }

  // Find the position of the empty tile
  const emptyIndex = tiles.indexOf(0);

  // Check if the puzzle is solvable
  if (!isSolvable(tiles, emptyIndex)) {
    // If not solvable, swap the first two non-empty tiles to change parity
    let i = 0, j = 1;
    // Find two non-empty tiles to swap
    while (i < tiles.length && tiles[i] === 0) i++;
    j = i + 1;
    while (j < tiles.length && tiles[j] === 0) j++;

    if (i < tiles.length && j < tiles.length) {
      [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
  }

  return { tiles, emptyIndex };
};

// For async solution calculation
const calculateSolutionAsync = async (board: BoardState): Promise<number[]> => {
  // Create a proper Web Worker implementation instead of using setTimeout
  return new Promise((resolve) => {
    // Create a string representation of the board for the worker
    const boardData = {
      tiles: board.tiles,
      emptyIndex: board.emptyIndex
    };

    // Check if Web Workers are supported
    if (typeof Worker !== 'undefined') {
      // Create a new worker
      const worker = new Worker(new URL('../../workers/puzzleSolver.worker.ts', import.meta.url), { type: 'module' });

      // Set up message handler to receive solution from worker
      worker.onmessage = (e) => {
        const solution = e.data.solution;
        worker.terminate(); // Clean up the worker
        resolve(solution);
      };

      // Handle errors
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        worker.terminate();
        resolve([]); // Return empty solution on error
      };

      // Send the board data to the worker
      worker.postMessage({ board: boardData });
    } else {
      // Fallback for browsers that don't support Web Workers
      console.warn('Web Workers not supported, falling back to setTimeout');
      setTimeout(() => {
        // We can't solve it here anymore since the algorithm is in the worker
        resolve([]);
      }, 10);
    }
  });
};

// Game reducer
const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'INITIALIZE_BOARD':
      return {
        ...state,
        board: {
          tiles: action.payload.tiles,
          emptyIndex: action.payload.emptyIndex
        },
        isCompleted: false,
        isPaused: false,
        hintedTile: null,
        calculatingSolution: false,
        hintRequested: false,
        showCompletionDialog: false,
        showConfirmDialog: false
        // Keep the existing difficultyLevel
      };

    case 'MOVE_TILE': {
      const newTiles = [...state.board.tiles];
      const { index, newEmptyIndex } = action.payload;

      // Swap tile with empty space
      newTiles[state.board.emptyIndex] = state.board.tiles[index];
      newTiles[index] = 0;

      return {
        ...state,
        board: {
          tiles: newTiles,
          emptyIndex: newEmptyIndex
        },
        hintedTile: null
      };
    }

    case 'INCREMENT_MOVES':
      return {
        ...state,
        stats: {
          ...state.stats,
          moves: state.stats.moves + 1
        }
      };

    case 'SET_COMPLETED':
      return {
        ...state,
        isCompleted: action.payload,
      };

    case 'SET_HINT':
      return {
        ...state,
        hintedTile: action.payload
      };

    case 'USE_HINT':
      return {
        ...state,
        stats: {
          ...state.stats,
          hintsUsed: state.stats.hintsUsed + 1
        },
        hintRequested: false
      };

    case 'TOGGLE_PAUSE':
      return {
        ...state,
        isPaused: !state.isPaused
      };

    case 'RESET_GAME':
      return {
        ...state,
        stats: action.payload,
        isCompleted: false,
        isPaused: false,
        hintedTile: null,
        hintRequested: false,
        calculatingSolution: false
      };

    case 'REQUEST_HINT':
      return {
        ...state,
        hintRequested: action.payload
      };

    case 'SET_CALCULATING_SOLUTION':
      return {
        ...state,
        calculatingSolution: action.payload
      };

    case 'SHOW_COMPLETION_DIALOG':
      return {
        ...state,
        showCompletionDialog: true
      };

    case 'CLOSE_COMPLETION_DIALOG':
      return {
        ...state,
        showCompletionDialog: false
      };

    case 'SHOW_CONFIRM_DIALOG':
      return {
        ...state,
        showConfirmDialog: true
      };

    case 'CLOSE_CONFIRM_DIALOG':
      return {
        ...state,
        showConfirmDialog: false
      };

    case 'UNDO_MOVE':
      return {
        ...state,
        board: action.payload.previousBoard,
        stats: {
          ...state.stats,
          moves: Math.max(0, state.stats.moves - 1)
        },
        hintedTile: null
      };

    case 'COMPLETE_GAME':
      return {
        ...state,
        isCompleted: true,
      };

    case 'WIN_GAME': {
      // Create a solved board state
      const solvedTiles = Array.from({ length: TILE_COUNT }, (_, i) => i + 1);
      solvedTiles.push(0); // Add empty tile at the end

      return {
        ...state,
        board: {
          tiles: solvedTiles,
          emptyIndex: TILE_COUNT // 15 for a 4x4 board
        },
        isCompleted: true,
        showCompletionDialog: true // Show completion dialog immediately
      };
    }

    case 'UPDATE_STATS':
      return {
        ...state,
        stats: action.payload
      };

    case 'SET_DIFFICULTY':
      // Verify this is properly updating difficulty
      return {
        ...state,
        difficultyLevel: action.payload // Make sure both are updated if both exist
      };

    default:
      return state;
  }
};

export const FifteenPuzzleGame = ({ initialSeed, puzzleDate, difficulty, isPreviewMode = false }: FifteenPuzzleGameProps = {}) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();

  // Add sound effects
  const { play: playMoveSound } = useSound('/assets/sounds/tile-move.mp3');
  const { play: playHintSound } = useSound('/assets/sounds/tile-move.mp3');
  const { play: playUndoSound } = useSound('/assets/sounds/tile-move.mp3');
  const { play: playStepCompleteSound } = useSound('/assets/sounds/step-complete.mp3');
  const { play: playPuzzleCompleteSound } = useSound('/assets/sounds/puzzle-complete.mp3');

  // State management
  const [currentSeed, setCurrentSeed] = useLocalStorage<number>('fifteen-puzzle-seed', 0);
  const [puzzleNumber, setPuzzleNumber] = useLocalStorage<number>('fifteen-puzzle-number', 1);
  const [lastPlayedDate, setLastPlayedDate] = useLocalStorage<string>('fifteen-puzzle-last-date', '');
  const initialPuzzleIdentity = useRef<DailyPuzzleIdentity>(
    createDailyPuzzleIdentity(
      '15puzzle',
      puzzleDate || new Date(),
      difficulty ?? 3,
      initialSeed
    )
  );
  const [activePuzzleIdentity, setActivePuzzleIdentity] = useState<DailyPuzzleIdentity>(initialPuzzleIdentity.current);

  // Add these state variables near your other useState declarations
  const [isSolving, setIsSolving] = useState<boolean>(false);
  const [solutionIndex, setSolutionIndex] = useState<number>(0);

  // Add a move history stack to track previous board states
  const [moveHistory, setMoveHistory] = useState<BoardState[]>([]);

  // Add these new state variables near your other useState declarations
  const [animatingTiles, setAnimatingTiles] = useState<{[key: number]: {from: number, to: number}}>({});
  const pointerStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const suppressNextClickRef = useRef(false);

  const currentPuzzleDate = useMemo(
    () => parseDateString(activePuzzleIdentity.date),
    [activePuzzleIdentity.date]
  );

  // Add a ref to track if the component has been initialized
  const isInitialized = useRef(false);

  // Move these state declarations BEFORE createInitialState
  const [solution, setSolution] = useState<number[]>([]);
  const [currentSolutionStep, setCurrentSolutionStep] = useState<number>(0);

  const [showNumbers, setShowNumbers] = useState<boolean>(() => {
    // Show numbers by default only for difficulty level 1 (easy)
    return difficulty === 1;
  });

  // Then your createInitialState function
  const createInitialState = useCallback((): GameState => {
    const expectedIdentity = initialPuzzleIdentity.current;
    const canRestoreSavedState = canRestorePuzzleState(
      localStorage.getItem(STORAGE_KEYS.IDENTITY),
      currentSeed || undefined,
      expectedIdentity
    );

    // Try to load saved state from localStorage first
    const savedBoardJSON = localStorage.getItem(STORAGE_KEYS.BOARD);
    const savedStatsJSON = localStorage.getItem(STORAGE_KEYS.STATS);
    const savedIsCompleted = localStorage.getItem(STORAGE_KEYS.IS_COMPLETED) === 'true';
    const savedMoveHistoryJSON = localStorage.getItem(STORAGE_KEYS.MOVE_HISTORY);
    const savedSolutionJSON = localStorage.getItem(STORAGE_KEYS.SOLUTION);
    const savedSolutionStep = localStorage.getItem(STORAGE_KEYS.SOLUTION_STEP);

    // If we have a saved board state and it matches our current seed, use it
    if (savedBoardJSON && canRestoreSavedState) {
      try {
        const savedBoard = JSON.parse(savedBoardJSON) as BoardState;
        const savedStats = savedStatsJSON ? JSON.parse(savedStatsJSON) as GameStats : { ...DEFAULT_GAME_STATS };

        // Load move history if available
        if (savedMoveHistoryJSON) {
          setMoveHistory(JSON.parse(savedMoveHistoryJSON));
        }

        // Load solution if available
        if (savedSolutionJSON) {
          setSolution(JSON.parse(savedSolutionJSON));
          if (savedSolutionStep) {
            setCurrentSolutionStep(parseInt(savedSolutionStep, 10));
          }
        }

        localStorage.setItem(STORAGE_KEYS.IDENTITY, JSON.stringify(expectedIdentity));

        // Return the saved state
        return {
          board: savedBoard,
          stats: savedStats,
          isCompleted: savedIsCompleted,
          isPaused: false, // Always start unpaused when returning
          hintedTile: null,
          hintRequested: false,
          calculatingSolution: false,
          showCompletionDialog: false,
          showConfirmDialog: false,
          difficultyLevel: difficulty ?? 3 // Set default difficulty to 3
        };
      } catch (error) {
        console.error('Error parsing saved game state:', error);
        // Fall back to generating a new board
      }
    }

    // If no saved state, error, or seed mismatch, generate a new board
    const seed = expectedIdentity.seed;
    setCurrentSeed(seed);
    setLastPlayedDate(expectedIdentity.date);
    if (expectedIdentity.date !== lastPlayedDate) {
      setPuzzleNumber(lastPlayedDate ? getNextPuzzleNumber(puzzleNumber) : 1);
    }

    const board = generateSolvableBoard(seed);

    // Save the new board to localStorage
    localStorage.setItem(STORAGE_KEYS.BOARD, JSON.stringify(board));
    localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(DEFAULT_GAME_STATS));
    localStorage.setItem(STORAGE_KEYS.IS_COMPLETED, 'false');
    localStorage.setItem(STORAGE_KEYS.MOVE_HISTORY, JSON.stringify([]));
    localStorage.setItem(STORAGE_KEYS.ELAPSED_TIME, '0');
    localStorage.setItem(STORAGE_KEYS.IDENTITY, JSON.stringify(expectedIdentity));
    localStorage.removeItem(STORAGE_KEYS.SOLUTION);
    localStorage.removeItem(STORAGE_KEYS.SOLUTION_STEP);
    localStorage.removeItem(STORAGE_KEYS.COMPLETION_TIME);
    localStorage.removeItem(STORAGE_KEYS.COMPLETED);

    return {
      board,
      stats: { ...DEFAULT_GAME_STATS },
      isCompleted: false,
      isPaused: false,
      hintedTile: null,
      hintRequested: false,
      calculatingSolution: false,
      showCompletionDialog: false,
      showConfirmDialog: false,
      difficultyLevel: difficulty ?? 3 // Set default difficulty to 3
    };
  }, [currentSeed, difficulty, lastPlayedDate, puzzleNumber, setCurrentSeed, setLastPlayedDate, setPuzzleNumber]);

  // Set up game state with reducer
  const [state, dispatch] = useReducer(gameReducer, null, createInitialState);
  const {
    board,
    stats,
    isCompleted,
    isPaused,
    hintRequested,
    calculatingSolution,
    showCompletionDialog,
    showConfirmDialog,
    difficultyLevel
  } = state;

  // Timer setup - load from localStorage if available
  const [elapsedTime, setElapsedTime] = useState(() => {
    const savedTime = localStorage.getItem(STORAGE_KEYS.ELAPSED_TIME);
    return savedTime ? parseInt(savedTime) : 0;
  });
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Create the ref outside the effect with the correct type
  const prevStateRef = useRef<{
    boardTiles: string;
    emptyIndex: number;
    isCompleted: boolean;
    isPaused: boolean;
    moves: number;
    solution: number;
    solutionStep: number;
    isSolving: boolean;
    solutionIndex: number;
  } | null>(null);

  // Save game state to localStorage whenever it changes
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    // Save board state
    localStorage.setItem(STORAGE_KEYS.BOARD, JSON.stringify(board));

    // Save game stats
    localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(stats));

    // Save completion status
    localStorage.setItem(STORAGE_KEYS.IS_COMPLETED, isCompleted.toString());

  }, [board, stats, isCompleted]);

  // Save move history when it changes
  useEffect(() => {
    if (isInitialized.current) {
      localStorage.setItem(STORAGE_KEYS.MOVE_HISTORY, JSON.stringify(moveHistory));
    }
  }, [moveHistory]);

  // Save solution and current step when they change
  useEffect(() => {
    if (isInitialized.current && solution.length > 0) {
      localStorage.setItem(STORAGE_KEYS.SOLUTION, JSON.stringify(solution));
      localStorage.setItem(STORAGE_KEYS.SOLUTION_STEP, currentSolutionStep.toString());
    }
  }, [solution, currentSolutionStep]);

  // Save elapsed time when it changes
  useEffect(() => {
    if (isInitialized.current) {
      localStorage.setItem(STORAGE_KEYS.ELAPSED_TIME, elapsedTime.toString());
    }
  }, [elapsedTime]);

  // Add a new state to track recently correctly placed tiles
  const [correctlyPlacedTiles, setCorrectlyPlacedTiles] = useState<{row: number, col: number, timestamp: number}[]>([]);

  // Update the handleTileClick function to create triple-flash animation effect
  const handleTileClick = useCallback((index: number) => {
    if (isPaused || isCompleted) return;

    if (isTileMovable(index, board.emptyIndex)) {
      // Play move sound
      playMoveSound();

      // Save current board state to history before making the move
      setMoveHistory(prev => [...prev, { ...board }]);

      // Set animation state before moving the tile
      setAnimatingTiles({
        [index]: { from: index, to: board.emptyIndex }
      });

      // Add a delay before updating the actual board state
      setTimeout(() => {
        // Get the tile value that's moving
        const tileValue = board.tiles[index];

        // Find where this tile will be after the move (the current empty index)
        const newEmptyIndex = index;

        // Move the tile
        dispatch({ type: 'MOVE_TILE', payload: { index, newEmptyIndex } });
        dispatch({ type: 'INCREMENT_MOVES' });

        // Check if the empty space is now in the correct position for THIS tile
        // For a tile with value N, its correct position is index N-1
        const isTileInFinalPosition = board.emptyIndex === tileValue - 1;

        // Play step completion sound and show triple-flash highlight if the tile is moved into its final position
        if (isTileInFinalPosition) {
          // Play sound with small delay
          setTimeout(() => {
            playStepCompleteSound();
          }, 50);

          // Add tile to correctly placed tiles for highlight animation
          const tileRow = Math.floor(board.emptyIndex / BOARD_SIZE);
          const tileCol = board.emptyIndex % BOARD_SIZE;

          // Triple flash effect:

          // First flash
          setCorrectlyPlacedTiles(prev => [
            ...prev,
            { row: tileRow, col: tileCol, timestamp: Date.now() }
          ]);

          // Second flash
          setTimeout(() => {
            setCorrectlyPlacedTiles(prev => {
              // Remove previous entries for this tile to avoid duplicates
              const filtered = prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol));
              return [...filtered, { row: tileRow, col: tileCol, timestamp: Date.now() }];
            });
          }, 500);

          // Third flash
          setTimeout(() => {
            setCorrectlyPlacedTiles(prev => {
              // Remove previous entries for this tile to avoid duplicates
              const filtered = prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol));
              return [...filtered, { row: tileRow, col: tileCol, timestamp: Date.now() }];
            });

            // Clear the highlight after the third flash
            setTimeout(() => {
              setCorrectlyPlacedTiles(prev =>
                prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol))
              );
            }, 500);
          }, 1000);
        }

        // Check if user's move matches the next move in the solution
        const nextSolutionTile = solution[currentSolutionStep];

        if (nextSolutionTile === index) {
          // User followed the solution, just advance the step without recalculating
          setCurrentSolutionStep(prev => prev + 1);
        } else if (solution.length > 0) {
          // User deviated from solution, need to recalculate
          setSolution([]);
          setCurrentSolutionStep(0);
        }

        // Clear animation state after the move is complete
        setTimeout(() => {
          setAnimatingTiles({});
        }, 18);
      }, 80);
    }
  }, [board, isPaused, isCompleted, solution, currentSolutionStep, playMoveSound, playStepCompleteSound]);

  const handleSaveGameToDatabase = useCallback(async (finalTime: number) => {
    try {
      const difficultyValue = activePuzzleIdentity.difficulty;
      const isPerfectSolve = stats.hintsUsed === 0 && !stats.numbersUsed;

      // Prepare game stats data
      const gameStatsData = {
        difficulty: difficultyValue,
        moves_made: stats.moves,
        hints_used: stats.hintsUsed,
        numbers_shown: stats.numbersUsed,
        perfect_solve: isPerfectSolve
      };

      // Save game data to database
      const { success } = await saveGameToDatabase(
        '15puzzle',
        currentPuzzleDate,
        difficultyValue,
        finalTime,
        gameStatsData,
        currentUser
      );

      if (success) {
        // Check if this is a personal best time
        if (isPerfectSolve) {
          const isPersonalBest = await checkPersonalBest('15puzzle', finalTime, isPerfectSolve, difficultyValue, currentUser);
          if (isPersonalBest) {
            toast.success(t('toastNewPersonalBest'), {
              position: 'top-center',
              duration: 3000
            });
          }
        }
      }
    } catch (err) {
      console.error('Error in handleSaveGameToDatabase:', err);
    }
  }, [activePuzzleIdentity.difficulty, currentPuzzleDate, currentUser, stats, t]);

  const handleGameComplete = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const finalTime = elapsedTime;
    localStorage.setItem(STORAGE_KEYS.COMPLETION_TIME, finalTime.toString());
    playPuzzleCompleteSound();

    setTimeout(() => {
      dispatch({ type: 'SHOW_COMPLETION_DIALOG' });
      handleSaveGameToDatabase(finalTime);
    }, 1500);
  }, [
    elapsedTime,
    handleSaveGameToDatabase,
    playPuzzleCompleteSound
  ]);

  // Update timer management effect to respect preview mode
  useEffect(() => {
    // Always clean up existing timer first
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start a new timer if not paused, not completed, and not in preview mode
    if (!state.isPaused && !state.isCompleted && !isPreviewMode) {
      timerRef.current = setInterval(createTimerTick(setElapsedTime, '15puzzle'), 1000);
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state.isPaused, state.isCompleted, isPreviewMode]);

  // Reset elapsed time if in preview mode
  useEffect(() => {
    if (isPreviewMode) {
      setElapsedTime(0);
    }
  }, [isPreviewMode]);

  // Add visibility change handler to auto-pause when tab loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !state.isPaused && !state.isCompleted) {
        dispatch({ type: 'TOGGLE_PAUSE' });

        // Manually clear the timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isPaused, state.isCompleted]);

  // Modify handlePauseToggle function (or create it if needed)
  const handlePauseToggle = () => {
    dispatch({ type: 'TOGGLE_PAUSE' });

    // Timer handling happens in the effect
  };
  const handleKeyboardMove = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    // Don't process moves if game is paused or completed or dropdown is open
    if (isPaused || isCompleted || document.getElementById('difficulty-dropdown-15puzzle')) return;

    // Find which tile can be moved in the specified direction
    const emptyIndex = board.emptyIndex;
    const boardSize = BOARD_SIZE; // 4 for a 4x4 grid

    let tileToMove = -1;

    // Calculate the row and column of the empty space
    const emptyRow = Math.floor(emptyIndex / boardSize);
    const emptyCol = emptyIndex % boardSize;

    // Determine which tile to move based on direction
    switch (direction) {
      case 'up':
        // Move the tile below the empty space up
        if (emptyRow < boardSize - 1) {
          tileToMove = emptyIndex + boardSize;
        }
        break;
      case 'down':
        // Move the tile above the empty space down
        if (emptyRow > 0) {
          tileToMove = emptyIndex - boardSize;
        }
        break;
      case 'left':
        // Move the tile to the right of the empty space left
        if (emptyCol < boardSize - 1) {
          tileToMove = emptyIndex + 1;
        }
        break;
      case 'right':
        // Move the tile to the left of the empty space right
        if (emptyCol > 0) {
          tileToMove = emptyIndex - 1;
        }
        break;
    }

    // If a valid tile to move was found, handle the tile click
    if (tileToMove !== -1) {
      handleTileClick(tileToMove);
    }
  }, [board.emptyIndex, handleTileClick, isCompleted, isPaused]);

  const completeGesture = useCallback((clientX: number, clientY: number) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) return;
    const horizontal = clientX - start.x;
    const vertical = clientY - start.y;
    if (Math.max(Math.abs(horizontal), Math.abs(vertical)) < 30) return;
    if (isPaused || isCompleted || document.getElementById('difficulty-dropdown-15puzzle')) return;
    suppressNextClickRef.current = true;
    handleKeyboardMove(
      Math.abs(horizontal) > Math.abs(vertical)
        ? (horizontal > 0 ? 'right' : 'left')
        : (vertical > 0 ? 'down' : 'up')
    );
  }, [handleKeyboardMove, isCompleted, isPaused]);

  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (isCompleted) setIsHoveringBoard(true);
    pointerStartRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    suppressNextClickRef.current = false;
  }, [isCompleted]);

  const handlePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const horizontal = event.clientX - start.x;
    const vertical = event.clientY - start.y;
    if (Math.max(Math.abs(horizontal), Math.abs(vertical)) < 30) return;
    // Capturing on pointerdown would redirect ordinary tile clicks to the board.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    completeGesture(event.clientX, event.clientY);
  }, [completeGesture]);

  const handlePointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    completeGesture(event.clientX, event.clientY);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    if (isCompleted) setIsHoveringBoard(false);
  }, [completeGesture, isCompleted]);

  const handlePointerCancel = useCallback((event: PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = null;
    suppressNextClickRef.current = false;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    if (isCompleted) setIsHoveringBoard(false);
  }, [isCompleted]);

  const handleBoardClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressNextClickRef.current) return;
    suppressNextClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Modify the debug effect to be less noisy
  useEffect(() => {
    // Create a simplified state object for comparison
    const currentState = {
      boardTiles: board.tiles.join(','),
      emptyIndex: board.emptyIndex,
      isCompleted,
      isPaused,
      moves: stats.moves,
      solution: solution.length,
      solutionStep: currentSolutionStep,
      isSolving,
      solutionIndex
    };

    // Only log if something important changed (not just the timer)
    if (!prevStateRef.current ||
        JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      // Update the previous state ref
      prevStateRef.current = currentState;
    }
  }, [board, isCompleted, isPaused, stats.moves, solution, currentSolutionStep, isSolving, solutionIndex]);

  // Fix the handleUndo function to play sound
  const handleUndo = useCallback(() => {
    if (moveHistory.length === 0 || isPaused || isCompleted) return;

    // Play undo sound
    playUndoSound();

    // Get the last board state
    const previousBoard = moveHistory[moveHistory.length - 1];

    // Find which tile moved by comparing the current board with the previous board
    let movedTileIndex = -1;
    for (let i = 0; i < board.tiles.length; i++) {
      if (board.tiles[i] === 0 && previousBoard.tiles[i] !== 0) {
        movedTileIndex = i;
        break;
      }
    }

    if (movedTileIndex !== -1) {
      // Set animation state for the undo move
      const tileValue = previousBoard.tiles[movedTileIndex];
      const currentPositionOfTile = board.tiles.indexOf(tileValue);

      setAnimatingTiles({
        [currentPositionOfTile]: { from: currentPositionOfTile, to: movedTileIndex }
      });

      // Add a delay before updating the actual board state
      setTimeout(() => {
        // Update the move history
        setMoveHistory(prev => prev.slice(0, -1));

        // Dispatch undo action
        dispatch({ type: 'UNDO_MOVE', payload: { previousBoard } });

        // Reset solution if we had one
        if (solution.length > 0) {
          setSolution([]);
          setCurrentSolutionStep(0);
        }

        // Clear animation state after the move is complete
        setTimeout(() => {
          setAnimatingTiles({});
        }, 18);
      }, 80);
    } else {
      // Fallback if we can't determine which tile moved
      setMoveHistory(prev => prev.slice(0, -1));
      dispatch({ type: 'UNDO_MOVE', payload: { previousBoard } });

      if (solution.length > 0) {
        setSolution([]);
        setCurrentSolutionStep(0);
      }
    }
  }, [moveHistory, isPaused, isCompleted, solution.length, board, playUndoSound]);

  const initializePuzzle = useCallback((identity: DailyPuzzleIdentity) => {
    const board = generateSolvableBoard(identity.seed);

    dispatch({ type: 'INITIALIZE_BOARD', payload: board });
    dispatch({ type: 'RESET_GAME', payload: { ...DEFAULT_GAME_STATS } });
    dispatch({ type: 'SET_DIFFICULTY', payload: identity.difficulty });
    setActivePuzzleIdentity(identity);
    setCurrentSeed(identity.seed);

    setElapsedTime(0);
    setSolution([]);
    setCurrentSolutionStep(0);
    setMoveHistory([]);
    setAnimatingTiles({});
    setIsSolving(false);
    setSolutionIndex(0);
    setShowNumbers(identity.difficulty === 1);

    localStorage.setItem(STORAGE_KEYS.BOARD, JSON.stringify(board));
    localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(DEFAULT_GAME_STATS));
    localStorage.setItem(STORAGE_KEYS.IS_COMPLETED, 'false');
    localStorage.setItem(STORAGE_KEYS.MOVE_HISTORY, JSON.stringify([]));
    localStorage.setItem(STORAGE_KEYS.ELAPSED_TIME, '0');
    localStorage.setItem(STORAGE_KEYS.IDENTITY, JSON.stringify(identity));
    localStorage.removeItem(STORAGE_KEYS.SOLUTION);
    localStorage.removeItem(STORAGE_KEYS.SOLUTION_STEP);
    localStorage.removeItem(STORAGE_KEYS.COMPLETION_TIME);
    localStorage.removeItem(STORAGE_KEYS.COMPLETED);
  }, [setCurrentSeed]);

  const handleResetGame = useCallback(() => {
    const newDifficultyStr = localStorage.getItem('fifteenPuzzleNewDifficulty');
    const currentDiff = newDifficultyStr
      ? parseInt(newDifficultyStr, 10)
      : difficultyLevel;
    const seedToUse = currentDiff === activePuzzleIdentity.difficulty
      ? activePuzzleIdentity.seed
      : getDateSeed(currentPuzzleDate, currentDiff);
    const identity = createDailyPuzzleIdentity(
      '15puzzle',
      currentPuzzleDate,
      currentDiff,
      seedToUse
    );

    initializePuzzle(identity);

  }, [
    activePuzzleIdentity.difficulty,
    activePuzzleIdentity.seed,
    difficultyLevel,
    currentPuzzleDate,
    initializePuzzle
  ]);

  const handleDailyDateCheck = useCallback((now: Date) => {
    const date = formatPuzzleDateString(now);
    if (date === activePuzzleIdentity.date) {
      return;
    }

    const identity = createDailyPuzzleIdentity('15puzzle', now, difficultyLevel);
    initializePuzzle(identity);
    setPuzzleNumber(getNextPuzzleNumber(puzzleNumber));
    setLastPlayedDate(identity.date);
  }, [
    activePuzzleIdentity.date,
    difficultyLevel,
    initializePuzzle,
    puzzleNumber,
    setLastPlayedDate,
    setPuzzleNumber
  ]);

  useDailyPuzzleRollover(initialSeed === undefined && !isPreviewMode, handleDailyDateCheck);

  // Update the handleConfirmReset function to properly handle difficulty changes
  const handleConfirmReset = useCallback(() => {
    // Get the confirmation type from localStorage
    const confirmationType = localStorage.getItem('fifteenPuzzleConfirmationType');

    if (confirmationType === 'difficulty') {
      // Get the new difficulty from localStorage
      const newDifficultyStr = localStorage.getItem('fifteenPuzzleNewDifficulty');
      const newDifficulty = newDifficultyStr ? parseInt(newDifficultyStr, 10) : difficultyLevel;

      // First close the dialog
      dispatch({ type: 'CLOSE_CONFIRM_DIALOG' });

      // Apply the new difficulty to the reducer
      if (newDifficulty !== difficultyLevel) {
        dispatch({ type: 'SET_DIFFICULTY', payload: newDifficulty });
      }

      // Add a small delay to ensure state is updated before reset
      setTimeout(() => {
        // Reset the game with the new difficulty
        handleResetGame();

        // Clear the localStorage items
        localStorage.removeItem('fifteenPuzzleConfirmationType');
        localStorage.removeItem('fifteenPuzzleNewDifficulty');
      }, 50);
    } else {
      // Handle regular reset
      dispatch({ type: 'CLOSE_CONFIRM_DIALOG' });
      handleResetGame();
    }
  }, [dispatch, handleResetGame, difficultyLevel]);

  // Fix the auto-solving effect
  useEffect(() => {
    // IMPORTANT: Only set up the timer if we're actually solving
    if (isSolving && solution.length > 0 && solutionIndex < solution.length) {
      const timer = setTimeout(() => {
        // Double-check we're still solving and have valid indices
        if (isSolving && solutionIndex < solution.length) {
          // Use a local variable to avoid dependency on handleTileClick
          const tileToMove = solution[solutionIndex];
          if (isTileMovable(tileToMove, board.emptyIndex)) {
            dispatch({ type: 'MOVE_TILE', payload: { index: tileToMove, newEmptyIndex: tileToMove } });
            dispatch({ type: 'INCREMENT_MOVES' });

            // Increment the solution index
            setSolutionIndex(prev => prev + 1);
          } else {
            // If tile is not movable, stop solving
            setIsSolving(false);
            setSolutionIndex(0);
          }
        } else {
          // We've reached the end of the solution
          setIsSolving(false);
          setSolutionIndex(0);
        }
      }, 300);

      return () => clearTimeout(timer);
    } else if (isSolving && (solution.length === 0 || solutionIndex >= solution.length)) {
      // If we're solving but have no solution or reached the end, stop solving
      setIsSolving(false);
      setSolutionIndex(0);
    }

    // Return a no-op cleanup function for when we're not solving
    return () => {};
  }, [isSolving, solution, solutionIndex, board.emptyIndex]);

  // Update the getTileAnimationStyle function to ensure it works correctly
  const getTileAnimationStyle = (tileIndex: number, tile: number) => {
    if (!tile || !animatingTiles[tileIndex]) return {};

    const { from, to } = animatingTiles[tileIndex];

    // Calculate row and column for positions
    const fromRow = Math.floor(from / BOARD_SIZE);
    const fromCol = from % BOARD_SIZE;
    const toRow = Math.floor(to / BOARD_SIZE);
    const toCol = to % BOARD_SIZE;

    // Calculate translation values
    const translateX = (toCol - fromCol) * 100;
    const translateY = (toRow - fromRow) * 100;

    // Return the animation name with the specific translation values embedded
    return {
      animation: `slideTile${translateX}_${translateY} 100ms ease-out forwards`
    };
  };

  // Update the CSS keyframes generation with the faster animation duration
  useEffect(() => {
    // Create a style element for our animations
    const style = document.createElement('style');

    // Generate all possible movement animations with 100ms duration
    const animations = [
      // Move right
      `@keyframes slideTile100_0 {
        0% { transform: translate(0%, 0%); }
        85% { transform: translate(100%, 0%) scale(1.05); }
        100% { transform: translate(100%, 0%) scale(1); }
      }`,
      // Move left
      `@keyframes slideTile-100_0 {
        0% { transform: translate(0%, 0%); }
        85% { transform: translate(-100%, 0%) scale(1.05); }
        100% { transform: translate(-100%, 0%) scale(1); }
      }`,
      // Move down
      `@keyframes slideTile0_100 {
        0% { transform: translate(0%, 0%); }
        85% { transform: translate(0%, 100%) scale(1.05); }
        100% { transform: translate(0%, 100%) scale(1); }
      }`,
      // Move up
      `@keyframes slideTile0_-100 {
        0% { transform: translate(0%, 0%); }
        85% { transform: translate(0%, -100%) scale(1.05); }
        100% { transform: translate(0%, -100%) scale(1); }
      }`
    ];

    style.textContent = animations.join('\n');
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Update the handleHint function to play sound
  const handleHint = useCallback(() => {
    // Don't do anything if game is paused or completed
    if (isPaused || isCompleted) {
      return;
    }

    // Track hint usage first with toast notification
    const newHintCount = stats.hintsUsed + 1;

    // Show toast message similar to SudokuGame
    toast.info(
      newHintCount === 1
        ? t('toastHintUsed')
        : t('toastHintUsedCount', { count: newHintCount }),
      { duration: 2000, position: 'top-center' }
    );

    // If we already have a solution and there are steps left, use it
    if (solution.length > 0 && currentSolutionStep < solution.length) {
      // Get the next move from the solution
      const nextTile = solution[currentSolutionStep];

      // Make sure the tile is movable
      if (isTileMovable(nextTile, board.emptyIndex)) {
        // Play hint sound
        playHintSound();

        // Save current board state to history before making the move
        setMoveHistory(prev => [...prev, { ...board }]);

        // Set animation state before moving the tile
        setAnimatingTiles({
          [nextTile]: { from: nextTile, to: board.emptyIndex }
        });

        // Add a delay before updating the actual board state
        setTimeout(() => {
          // Get the tile value that's moving
          const tileValue = board.tiles[nextTile];

          // Move the tile
          dispatch({ type: 'MOVE_TILE', payload: { index: nextTile, newEmptyIndex: nextTile } });
          dispatch({ type: 'INCREMENT_MOVES' });
          dispatch({ type: 'USE_HINT' });

          // Check if the empty space is now in the correct position for THIS tile
          const isTileInFinalPosition = board.emptyIndex === tileValue - 1;

          // Play step completion sound and show triple-flash highlight if the tile is moved into its final position
          if (isTileInFinalPosition) {
            // Play sound with small delay
            setTimeout(() => {
              playStepCompleteSound();
            }, 50);

            // Add tile to correctly placed tiles for highlight animation
            const tileRow = Math.floor(board.emptyIndex / BOARD_SIZE);
            const tileCol = board.emptyIndex % BOARD_SIZE;

            // Triple flash effect:

            // First flash
            setCorrectlyPlacedTiles(prev => [
              ...prev,
              { row: tileRow, col: tileCol, timestamp: Date.now() }
            ]);

            // Second flash
            setTimeout(() => {
              setCorrectlyPlacedTiles(prev => {
                // Remove previous entries for this tile to avoid duplicates
                const filtered = prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol));
                return [...filtered, { row: tileRow, col: tileCol, timestamp: Date.now() }];
              });
            }, 500);

            // Third flash
            setTimeout(() => {
              setCorrectlyPlacedTiles(prev => {
                // Remove previous entries for this tile to avoid duplicates
                const filtered = prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol));
                return [...filtered, { row: tileRow, col: tileCol, timestamp: Date.now() }];
              });

              // Clear the highlight after the third flash
              setTimeout(() => {
                setCorrectlyPlacedTiles(prev =>
                  prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol))
                );
              }, 500);
            }, 1000);
          }

          // Advance to next step in solution
          setCurrentSolutionStep(prev => prev + 1);

          // Clear animation state after the move is complete
          setTimeout(() => {
            setAnimatingTiles({});
          }, 18);
        }, 80);
      }
    }
    // Otherwise, we need to calculate a new solution
    else if (!calculatingSolution) {
      // Set flags to indicate we're calculating
      dispatch({ type: 'SET_CALCULATING_SOLUTION', payload: true });
      dispatch({ type: 'REQUEST_HINT', payload: true });

      // Calculate the solution asynchronously
      calculateSolutionAsync(board)
        .then(newSolution => {
          // Update the solution state
          setSolution(newSolution);
          setCurrentSolutionStep(0);

          // Reset the calculating flag
          dispatch({ type: 'SET_CALCULATING_SOLUTION', payload: false });

          // If we found a solution, apply the first step
          if (newSolution.length > 0) {
            const nextTile = newSolution[0];

            if (isTileMovable(nextTile, board.emptyIndex)) {
              // Play hint sound
              playHintSound();

              // Save current board state to history before making the move
              setMoveHistory(prev => [...prev, { ...board }]);

              // Set animation state before moving the tile
              setAnimatingTiles({
                [nextTile]: { from: nextTile, to: board.emptyIndex }
              });

              // Add a delay before updating the actual board state
              setTimeout(() => {
                // Get the tile value that's moving
                const tileValue = board.tiles[nextTile];

                // Move the tile
                dispatch({ type: 'MOVE_TILE', payload: { index: nextTile, newEmptyIndex: nextTile } });
                dispatch({ type: 'INCREMENT_MOVES' });
                dispatch({ type: 'USE_HINT' });

                // Check if the empty space is now in the correct position for THIS tile
                const isTileInFinalPosition = board.emptyIndex === tileValue - 1;

                // Play step completion sound and show triple-flash highlight if the tile is moved into its final position
                if (isTileInFinalPosition) {
                  // Play sound with small delay
                  setTimeout(() => {
                    playStepCompleteSound();
                  }, 50);

                  // Add tile to correctly placed tiles for highlight animation
                  const tileRow = Math.floor(board.emptyIndex / BOARD_SIZE);
                  const tileCol = board.emptyIndex % BOARD_SIZE;

                  // Triple flash effect:

                  // First flash
                  setCorrectlyPlacedTiles(prev => [
                    ...prev,
                    { row: tileRow, col: tileCol, timestamp: Date.now() }
                  ]);

                  // Second flash
                  setTimeout(() => {
                    setCorrectlyPlacedTiles(prev => {
                      // Remove previous entries for this tile to avoid duplicates
                      const filtered = prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol));
                      return [...filtered, { row: tileRow, col: tileCol, timestamp: Date.now() }];
                    });
                  }, 500);

                  // Third flash
                  setTimeout(() => {
                    setCorrectlyPlacedTiles(prev => {
                      // Remove previous entries for this tile to avoid duplicates
                      const filtered = prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol));
                      return [...filtered, { row: tileRow, col: tileCol, timestamp: Date.now() }];
                    });

                    // Clear the highlight after the third flash
                    setTimeout(() => {
                      setCorrectlyPlacedTiles(prev =>
                        prev.filter(tile => !(tile.row === tileRow && tile.col === tileCol))
                      );
                    }, 500);
                  }, 1000);
                }

                // Advance to next step in solution
                setCurrentSolutionStep(prev => prev + 1);

                // Clear animation state after the move is complete
                setTimeout(() => {
                  setAnimatingTiles({});
                }, 18);
              }, 80);
            }
          }
        })
        .catch(error => {
          console.error('Error calculating solution:', error);
          dispatch({ type: 'SET_CALCULATING_SOLUTION', payload: false });
          dispatch({ type: 'REQUEST_HINT', payload: false });
        });
    }
  }, [board, solution, currentSolutionStep, isPaused, isCompleted, calculatingSolution, playHintSound, playStepCompleteSound, stats.hintsUsed, t]);

  // Modify the handleNewGame function to check for actual moves
  const handleNewGame = useCallback(() => {
    // Only show confirmation if the user has made actual moves
    if (!isCompleted && stats.moves > 0) {
      dispatch({ type: 'SHOW_CONFIRM_DIALOG' });
    } else {
      handleResetGame();
    }
  }, [isCompleted, stats.moves, handleResetGame]);

  const [incompleteGames, setIncompleteGames] = useState<DailyPuzzleGame[]>([
    'sudoku',
    '2048'
  ]);
  const allGamesCompleted = incompleteGames.length === 0;

  useEffect(() => {
    if (showCompletionDialog) {
      const checkAllGames = async () => {
        const games = await getIncompleteDailyGames(currentPuzzleDate, '15puzzle', currentUser);
        setIncompleteGames(games);
      };

      checkAllGames();
    }
  }, [showCompletionDialog, currentPuzzleDate, currentUser]);

  // Add function to handle the win button click
  const handleWin = useCallback(() => {
    // Dispatch the action to immediately solve the puzzle
    dispatch({ type: 'WIN_GAME' });

    // Store completion in localStorage
    localStorage.setItem(STORAGE_KEYS.COMPLETED, 'true');

    // Save game completion to database (showNumbers is already part of stats)
    handleSaveGameToDatabase(elapsedTime);

    // Show completion dialog
    dispatch({ type: 'SHOW_COMPLETION_DIALOG' });
  }, [elapsedTime, dispatch, handleSaveGameToDatabase]);

  useEffect(() => {
    // Check if the game is completed
    if (board.tiles.every((tile, index) => {
      // The empty space (0) should be at the bottom right (index 15)
      if (tile === 0) return index === 15;
      // All other tiles should be in order (index + 1)
      return tile === index + 1;
    })) {
      // Game is completed!
      if (!isCompleted) {
        // Play completion sound
        playPuzzleCompleteSound();

        // Set completed state
        dispatch({ type: 'COMPLETE_GAME' });

        // Store completion in localStorage
        localStorage.setItem(STORAGE_KEYS.COMPLETED, 'true');

        // Call the centralized completion handler
        handleGameComplete();

        // Show completion dialog
        dispatch({ type: 'SHOW_COMPLETION_DIALOG' });
      }
    }
  }, [
    board.tiles,
    isCompleted,
    playPuzzleCompleteSound,
    dispatch,
    handleGameComplete
  ]);

  const getPuzzleImageIndex = (date: Date): number => {
    // Reference date: April 1st, 2025
    const referenceDate = new Date(2025, 3, 1); // Month is 0-indexed, so 3 = April

    // Calculate days difference
    const timeDiff = date.getTime() - referenceDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

    // Handle dates before reference date
    const dayCount = daysDiff >= 0 ? daysDiff : 0;

    // Assuming we have a maximum of 10 images (adjust as needed)
    // TODO: In production, this should dynamically check available images
    const MAX_IMAGES = 49;
    return (dayCount % MAX_IMAGES) + 1;
  };

  // Add state for the image
  const [imageLoaded, setImageLoaded] = useState(false);
  // Store the successfully loaded path with extension
  const [puzzleImageWithExt, setPuzzleImageWithExt] = useState('');

  // Calculate image path based on date
  const puzzleImage = useMemo(() => {
    const imageIndex = getPuzzleImageIndex(currentPuzzleDate);
    // Try both extensions - the one that works in the loader will be used
    const basePath = `/assets/images/15puzzle-${imageIndex}`;
    //setPuzzleImageWithExt(basePath + '.jpg'); // Set default extension initially
    return basePath;
  }, [currentPuzzleDate]);

  // Update the imageZoomStyle hook to separate blur from scale
  const imageZoomStyle = useMemo(() => {
    // Default zoom level (difficulty 3)
    let scale = 3;
    let blur = '0px'; // No blur by default

    // Adjust based on difficulty
    if (difficultyLevel) {
      switch (difficultyLevel) {
        case 1: // Easy - show almost full image
          scale = 1.0;
          blur = '0px';
          break;
        case 2: // Medium - moderate zoom
          scale = 1.5;
          blur = '0px';
          break;
        case 3: // Hard - more zoom
          scale = 3.0;
          blur = '0px';
          break;
        case 4: // Expert - extreme zoom + blur for background only
          scale = 5.0;
          blur = '1px'; // Blur only the background image
          break;
      }
    }

    return { scale, blur };
  }, [difficultyLevel]);

  // Add these state variables for image dimensions
  const [imageWidth, setImageWidth] = useState<number>(0);
  const [imageHeight, setImageHeight] = useState<number>(0);
  const [imageDimensionsLoaded, setImageDimensionsLoaded] = useState<boolean>(false);

  // Preload the image with multiple format support
  useEffect(() => {
    setImageLoaded(false);
    setImageDimensionsLoaded(false);
    setPuzzleImageWithExt('');

    // Try different file extensions
    const tryLoadWithExtension = (extensions: string[], currentIndex = 0) => {
      if (currentIndex >= extensions.length) {
        console.error('Failed to load image with any extension');
        setImageLoaded(true); // Allow the game to continue without image
        return;
      }

      const ext = extensions[currentIndex];
      const fullPath = `${puzzleImage}.${ext}`;

      const img = new Image();
      img.src = fullPath;

      img.onload = () => {
        // Store the image dimensions
        setImageWidth(img.width);
        setImageHeight(img.height);
        setImageDimensionsLoaded(true);

        // Update the puzzleImage ref to include the extension
        setPuzzleImageWithExt(fullPath);
        setImageLoaded(true);
      };

      img.onerror = () => {
        // Try the next extension
        tryLoadWithExtension(extensions, currentIndex + 1);
      };
    };

    // Try loading with these extensions in order
    tryLoadWithExtension(['jpg', 'png', 'jpeg']);

    return () => {
      // No specific cleanup needed here
    };
  }, [puzzleImage, currentPuzzleDate]);

  // Revert back to the previous scaling calculation
  const optimalImageScaling = useMemo(() => {
    if (!imageDimensionsLoaded || imageWidth === 0 || imageHeight === 0) {
      // Return default scaling if dimensions aren't available
      return {
        backgroundSizeValue: `${imageZoomStyle.scale * 400}%`,
        aspectRatio: 1,
        scale: imageZoomStyle.scale * 400,
        xOffset: 0,
        yOffset: 0
      };
    }

    // Calculate aspect ratio of the image
    const aspectRatio = imageWidth / imageHeight;

    // Base scale for a 4-tile grid is 400%
    const baseScale = 400;

    // Calculate scaling and offset values to center the image
    let backgroundSizeValue = '';
    let xOffset = 0; // Horizontal offset as percentage
    let yOffset = 0; // Vertical offset as percentage

    if (aspectRatio > 1) {
      // Landscape image (wider than tall)
      // For landscapes, we scale based on height to ensure height fits exactly
      const zoomFactor = imageZoomStyle.scale;

      // When zooming in (difficulty > 1), we want to zoom into the center
      // First calculate what the width would be at difficulty 1 (zoomed out)
      const unzoomedHeight = baseScale;
      const unzoomedWidth = aspectRatio * unzoomedHeight;

      // Calculate what the width will be at the current zoom level
      const zoomedHeight = unzoomedHeight * zoomFactor;
      const zoomedWidth = unzoomedWidth * zoomFactor;

      // Calculate the centering offset for both the aspectRatio and zoom
      // This ensures we always zoom into the center of the image
      if (zoomedWidth > baseScale) {
        // When zoomed, we need to center horizontally
        // We first compute the excess width as a percentage of the zoomed width
        const excessWidth = (zoomedWidth - baseScale) / 2;
        // Then convert to a percentage of the zoomed width to use as CSS offset
        xOffset = (excessWidth / zoomedWidth) * 100;
      }

      backgroundSizeValue = `auto ${zoomedHeight}%`;

    } else if (aspectRatio < 1) {
      // Portrait image (taller than wide)
      // For portraits, we scale based on width to ensure width fits exactly
      const zoomFactor = imageZoomStyle.scale;

      // First calculate what the height would be at difficulty 1 (zoomed out)
      const unzoomedWidth = baseScale;
      const unzoomedHeight = unzoomedWidth / aspectRatio;

      // Calculate what the height will be at the current zoom level
      const zoomedWidth = unzoomedWidth * zoomFactor;
      const zoomedHeight = unzoomedHeight * zoomFactor;

      // Calculate the centering offset for both the aspectRatio and zoom
      if (zoomedHeight > baseScale) {
        // When zoomed, we need to center vertically
        const excessHeight = (zoomedHeight - baseScale) / 2;
        yOffset = (excessHeight / zoomedHeight) * 100;
      }

      backgroundSizeValue = `${zoomedWidth}% auto`;

    } else {
      // Square image - just apply the zoom
      const zoomedSize = baseScale * imageZoomStyle.scale;
      backgroundSizeValue = `${zoomedSize}%`;
    }

    return {
      backgroundSizeValue,
      aspectRatio,
      scale: imageZoomStyle.scale * baseScale,
      xOffset,
      yOffset
    };
  }, [imageWidth, imageHeight, imageDimensionsLoaded, imageZoomStyle.scale]);

  // Update the getTileStyle function to separate the blur effect from the tile numbers
  const getTileStyle = (index: number, tile: number) => {
    if (!tile) return {};

    // For the image-based tiles, calculate background position
    const tileRow = Math.floor((tile - 1) / BOARD_SIZE);
    const tileCol = (tile - 1) % BOARD_SIZE;

    // Apply animation style if this tile is being animated
    const animationStyle = getTileAnimationStyle(index, tile);

    // If game is completed and user is hovering, maintain the same tile view
    // but reduce opacity to allow the full overlay image to be visible
    if (isCompleted && isHoveringBoard) {
      // Get offsets to center the image if needed
      const { xOffset, yOffset } = imageDimensionsLoaded
        ? optimalImageScaling
        : { xOffset: 0, yOffset: 0 };

      // Keep the exact same positioning and scaling as normal state
      // so tiles don't jump around during hover transition
      const backgroundPos = `${-tileCol * 100 + xOffset}% ${-tileRow * 100 + yOffset}%`;

      return {
        backgroundImage: `url(${puzzleImageWithExt})`,
        backgroundSize: imageDimensionsLoaded
          ? optimalImageScaling.backgroundSizeValue
          : `${imageZoomStyle.scale * 360}%`,
        backgroundPosition: backgroundPos,
        // Remove blur filter from here
        transition: 'opacity 0.3s ease-in-out',
        opacity: 0.2, // Just fade out the tiles
        ...animationStyle
      };
    }

    // Get offsets to center the image if needed
    const { xOffset, yOffset } = imageDimensionsLoaded
      ? optimalImageScaling
      : { xOffset: 0, yOffset: 0 };

    // Calculate background position with centering offsets
    const backgroundPos = `${-tileCol * 100 + xOffset}% ${-tileRow * 100 + yOffset}%`;

    // Use the optimized background size format for normal tiles
    const style = {
      backgroundImage: `url(${puzzleImageWithExt})`,
      backgroundSize: imageDimensionsLoaded
        ? optimalImageScaling.backgroundSizeValue
        : `${imageZoomStyle.scale * 360}%`,
      backgroundPosition: backgroundPos,
      // Remove blur filter from here
      transition: isCompleted ? 'opacity 0.3s ease-in-out' : 'none',
      ...animationStyle
    };

    return style;
  };

  // Add state for image hover
  const [isHoveringBoard, setIsHoveringBoard] = useState(false);

  // To handle blurred backgrounds
  useEffect(() => {
    // Create a style element for the tile blur effect
    const style = document.createElement('style');

    style.textContent = `
      .tile-with-blur-bg::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: inherit;
        background-size: inherit;
        background-position: inherit;
        filter: blur(${imageZoomStyle.blur});
        z-index: 0;
        border-radius: inherit;
      }

      .tile-with-blur-bg > span {
        z-index: 1;
        position: relative;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [imageZoomStyle.blur]);

  // Add a new state to track if dropdown exists
  const [dropdownExists, setDropdownExists] = useState(false);

  // Update the toggleDifficultyDropdown function to set the dropdown state
  const toggleDifficultyDropdown = (
    currentDifficulty: number,
    t: TFunction,
    _currentPuzzleDate: Date,
    stats: GameStats,
    isCompleted: boolean
  ) => {
    // Check if dropdown already exists and remove it if it does
    let dropdown = document.getElementById('difficulty-dropdown-15puzzle');
    if (dropdown) {
      document.body.removeChild(dropdown);
      setDropdownExists(false);
      return;
    }

    // Create dropdown if it doesn't exist
    dropdown = document.createElement('div');
    dropdown.id = 'difficulty-dropdown-15puzzle';
    dropdown.className = 'fixed bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50';
    // Set tabIndex to make it focusable for keyboard navigation
    dropdown.tabIndex = 0;

    // Set dropdown state to true
    setDropdownExists(true);

    // Create header
    const header = document.createElement('div');
    header.className = 'bg-gray-100 dark:bg-gray-700 px-4 py-2 font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 rounded-t-md';
    header.textContent = t('difficulty');
    dropdown.appendChild(header);

    // Create menu content
    const content = document.createElement('div');
    content.className = 'py-1';

    // Add options and track created buttons
    const buttons: HTMLButtonElement[] = [];

    const options = [
      { value: 1, label: t('easy') },
      { value: 2, label: t('medium') },
      { value: 3, label: t('hard') },
      { value: 4, label: t('expert') }
    ];

    options.forEach(option => {
      const button = document.createElement('button');
      button.className = `${
        currentDifficulty === option.value
          ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-100'
          : 'text-gray-700 dark:text-gray-200'
      } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700`;
      button.textContent = option.label;

      // Add the button to our tracking array
      buttons.push(button);

      // Add click handler
      button.addEventListener('click', () => {
        // Remove the dropdown
        document.body.removeChild(dropdown);
        // Reset dropdown state
        setDropdownExists(false);

        // Only change difficulty if it's different
        if (currentDifficulty !== option.value) {
          // Store the confirmation type
          localStorage.setItem('fifteenPuzzleConfirmationType', 'difficulty');
          localStorage.setItem('fifteenPuzzleNewDifficulty', option.value.toString());

          // Show confirmation dialog if game is in progress and not completed
          if (!isCompleted && (stats.moves > 0 || elapsedTime > 0)) {
            dispatch({ type: 'SHOW_CONFIRM_DIALOG' });
            // Store the new difficulty to apply after confirmation
          } else {
            // Apply immediately if game hasn't started or is completed
            dispatch({ type: 'SET_DIFFICULTY', payload: option.value });
            // Reset the game immediately
            handleResetGame();
          }
        }
      });

      content.appendChild(button);
    });

    dropdown.appendChild(content);

    // Position the dropdown
    const buttonRect = document.getElementById('difficulty-button-15puzzle')?.getBoundingClientRect();
    if (buttonRect) {
      dropdown.style.top = `${buttonRect.bottom + window.scrollY}px`;
      dropdown.style.left = `${buttonRect.left + window.scrollX}px`;
      dropdown.style.width = '200px';
    }

    document.body.appendChild(dropdown);

    // Find the index of the current difficulty in our options array
    let currentFocusIndex = 0;
    for (let i = 0; i < options.length; i++) {
      if (options[i].value === currentDifficulty) {
        currentFocusIndex = i;
        break;
      }
    }

    // Focus the button corresponding to the current difficulty
    buttons[currentFocusIndex].focus();

    // Track current focus index
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % buttons.length;
          (buttons[currentFocusIndex] as HTMLButtonElement).focus();
          break;

        case 'ArrowUp':
          e.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + buttons.length) % buttons.length;
          (buttons[currentFocusIndex] as HTMLButtonElement).focus();
          break;

        case 'Enter':
        case ' ': // Space key
          e.preventDefault();
          (buttons[currentFocusIndex] as HTMLButtonElement).click();
          break;

        case 'Escape':
          e.preventDefault();
          document.body.removeChild(dropdown);
          // Reset dropdown state
          setDropdownExists(false);
          // Return focus to the difficulty button
          document.getElementById('difficulty-button-15puzzle')?.focus();
          break;
      }
    };

    dropdown.addEventListener('keydown', handleKeyDown);

    // Add event listener to close on outside click
    const closeDropdown = (event: MouseEvent) => {
      if (dropdown &&
          !dropdown.contains(event.target as Node) &&
          event.target !== document.getElementById('difficulty-button-15puzzle')) {
        if (document.body.contains(dropdown)) {
          document.body.removeChild(dropdown);
          // Clean up event listeners
          dropdown.removeEventListener('keydown', handleKeyDown);
          // Update dropdown state
          setDropdownExists(false);
        }
        document.removeEventListener('click', closeDropdown);
      }
    };

    // Use setTimeout to prevent immediate closure
    setTimeout(() => {
      document.addEventListener('click', closeDropdown);
    }, 0);
  };

  const [showKeyboardControls, setShowKeyboardControls] = useState(false);
  const keyboardActionsRef = useRef({
    handleKeyboardMove,
    handlePauseToggle,
    toggleDifficultyDropdown
  });
  keyboardActionsRef.current = {
    handleKeyboardMove,
    handlePauseToggle,
    toggleDifficultyDropdown
  };

  // Now the useEffect that depends on toggleDifficultyDropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore keyboard events when dialogs are open
      if (showCompletionDialog || showConfirmDialog) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          keyboardActionsRef.current.handleKeyboardMove('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          keyboardActionsRef.current.handleKeyboardMove('down');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          keyboardActionsRef.current.handleKeyboardMove('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          keyboardActionsRef.current.handleKeyboardMove('right');
          break;
        case ' ': // Space key
          e.preventDefault();
          if (!isCompleted) {
            keyboardActionsRef.current.handlePauseToggle();
          }
          break;
        case 'u': // 'u' key for undo
        case 'U':
          if (!isPaused && !isCompleted && moveHistory.length > 0) {
            handleUndo();
          }
          break;
        case 'h': // 'h' key for hint
        case 'H':
          if (!isPaused && !isCompleted) {
            handleHint();
          }
          break;
        case 'n': // 'n' key for toggle numbers
        case 'N':
          if (!isPaused && !isCompleted) {
            const newShowNumbers = !showNumbers;
            setShowNumbers(newShowNumbers);

            // If turning numbers on and they weren't used before, track it
            if (newShowNumbers && !stats.numbersUsed) {
              dispatch({
                type: 'UPDATE_STATS',
                payload: { ...stats, numbersUsed: true }
              });

              // Show toast message
              toast.info(t('toastNumbersUsed'), {
                duration: 2000,
                position: 'top-center'
              });
            }
          }
          break;
        case 'r': // 'r' key for reset
        case 'R':
          if ((e.ctrlKey || e.metaKey) && !isPaused) {
            e.preventDefault();
            handleNewGame();
          }
          break;
        case 'd': // 'd' key for difficulty change
        case 'D':
          if ((e.ctrlKey || e.metaKey) && !isPaused && !dropdownExists) {
            e.preventDefault();
            // Call the toggleDifficultyDropdown function directly
            keyboardActionsRef.current.toggleDifficultyDropdown(
              difficultyLevel,
              t,
              currentPuzzleDate,
              stats,
              isCompleted
            );
          }
          break;
        case 'k': // 'k' key for toggling keyboard controls
        case 'K':
          e.preventDefault();
          setShowKeyboardControls(prev => !prev);
          break;
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    handleUndo,
    handleHint,
    handleNewGame,
    isPaused,
    isCompleted,
    moveHistory.length,
    showNumbers,
    stats,
    showCompletionDialog,
    showConfirmDialog,
    difficultyLevel,
    t,
    currentPuzzleDate,
    showKeyboardControls,
    dropdownExists
  ]);

  // Add an event listener for the custom event to show the confirm dialog
  useEffect(() => {
    const showConfirmDialogHandler = () => {
      dispatch({ type: 'SHOW_CONFIRM_DIALOG' });
    };

    document.addEventListener('showConfirmDialog', showConfirmDialogHandler);

    return () => {
      document.removeEventListener('showConfirmDialog', showConfirmDialogHandler);
    };
  }, []);

  const setupDialogKeyboardNavigationRef = useRef<
    (
      dialogId: string,
      buttonSelector: string,
      onEnterPress: (selectedButton: HTMLButtonElement) => void
    ) => () => void
  >(() => () => {});

  useEffect(() => {
    if (!showConfirmDialog && !showCompletionDialog) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    return () => { if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [showConfirmDialog, showCompletionDialog]);

  // Update the useEffect for the reset confirmation dialog
  useEffect(() => {
    if (showConfirmDialog) {
      // Add unique ID to the dialog for targeting
      setTimeout(() => {
        const dialog = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50.flex.items-center.justify-center.z-50');
        if (dialog && !dialog.id) {
          dialog.id = 'fifteen-puzzle-reset-dialog';

          // Setup keyboard navigation
          const cleanup = setupDialogKeyboardNavigationRef.current(
            'fifteen-puzzle-reset-dialog',
            'button',
            (button) => {
              // Determine which button was selected and trigger the appropriate action
              if (button.textContent?.includes(t('confirm'))) {
                handleConfirmReset();
              } else {
                dispatch({ type: 'CLOSE_CONFIRM_DIALOG' });
              }
            }
          );

          // Return cleanup function
          return cleanup;
        }
      }, 0);
    }
  }, [showConfirmDialog, handleConfirmReset, t]);

  // Add this function to handle keyboard navigation in dialogs
  const setupDialogKeyboardNavigation = (
    dialogId: string,
    buttonSelector: string,
    onEnterPress: (selectedButton: HTMLButtonElement) => void
  ) => {
    // Find all buttons in the dialog
    const dialog = document.getElementById(dialogId);
    if (!dialog) return () => {};

    const buttons = dialog.querySelectorAll<HTMLButtonElement>(buttonSelector);
    if (buttons.length === 0) return () => {};

    const previousFocus = document.activeElement as HTMLElement | null;

    // Add a custom focus style to all buttons
    buttons.forEach(button => {
      // Store original styles
      const originalBgColor = window.getComputedStyle(button).backgroundColor;
      const originalTextColor = window.getComputedStyle(button).color;

      // Save original styles as data attributes
      button.dataset.originalBg = originalBgColor;
      button.dataset.originalText = originalTextColor;

      // Add focus event
      button.addEventListener('focus', () => {
        // Apply focus style
        if (!button.className.includes('bg-blue-500')) {
          button.style.backgroundColor = '#4F46E5'; // indigo-600
          button.style.color = 'white';
        }
        button.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.3)';
      });

      // Add blur event
      button.addEventListener('blur', () => {
        // Restore original style
        button.style.backgroundColor = button.dataset.originalBg || '';
        button.style.color = button.dataset.originalText || '';
        button.style.boxShadow = '';
      });
    });

    // Find the confirm button and set initial focus on it
    let confirmButtonIndex = 0;
    for (let i = 0; i < buttons.length; i++) {
      if (buttons[i].textContent?.includes(t('confirm'))) {
        confirmButtonIndex = i;
        break;
      }
    }

    // Set initial focus on the confirm button
    buttons[confirmButtonIndex].focus();

    // Track current focus index
    let currentFocusIndex = confirmButtonIndex;

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Tab': {
          e.preventDefault();
          const focusedIndex = Array.from(buttons).indexOf(document.activeElement as HTMLButtonElement);
          currentFocusIndex = (focusedIndex + (e.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
          buttons[currentFocusIndex].focus();
          break;
        }
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % buttons.length;
          buttons[currentFocusIndex].focus();
          break;

        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + buttons.length) % buttons.length;
          buttons[currentFocusIndex].focus();
          break;

        case 'Enter':
        case ' ': // Space key
          e.preventDefault();
          onEnterPress(buttons[currentFocusIndex]);
          break;

        case 'Escape':
          e.preventDefault();
          // Find and click the cancel button
          for (let i = 0; i < buttons.length; i++) {
            if (buttons[i].textContent?.includes(t('cancel')) ||
                buttons[i].textContent?.includes(t('close'))) {
              buttons[i].click();
              break;
            }
          }
          break;
      }
    };

    // Add the event listener to the dialog
    dialog.addEventListener('keydown', handleKeyDown);

    // Return a cleanup function
    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();

      // Clean up focus styles
      buttons.forEach(button => {
        button.removeEventListener('focus', () => {});
        button.removeEventListener('blur', () => {});
        button.style.backgroundColor = button.dataset.originalBg || '';
        button.style.color = button.dataset.originalText || '';
        button.style.boxShadow = '';
      });
    };
  };
  setupDialogKeyboardNavigationRef.current = setupDialogKeyboardNavigation;

  // Update the useEffect for the completion dialog
  useEffect(() => {
    if (showCompletionDialog) {
      // Add unique ID to the dialog for targeting
      setTimeout(() => {
        const dialog = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50.flex.items-center.justify-center.z-50');
        if (dialog && !dialog.id) {
          dialog.id = 'fifteen-puzzle-completion-dialog';

          // Setup keyboard navigation
          const cleanup = setupDialogKeyboardNavigationRef.current(
            'fifteen-puzzle-completion-dialog',
            'button',
            (button) => {
              // Determine which button was selected and trigger the appropriate action
              if (button.textContent?.includes(t('close'))) {
                dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
              } else if (button.textContent?.includes(t('tryAgain'))) {
                dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                handleResetGame();
              } else if (button.textContent?.includes(t('tryTodays15Puzzle'))) {
                dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                window.location.href = '/15puzzle';
              } else if (
                button.textContent?.includes(t('tryTodaysSudoku')) ||
                button.textContent?.includes(t('trySudokuForDate'))
              ) {
                dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                window.location.href = `/sudoku/${currentSeed}`;
              } else if (
                button.textContent?.includes(t('tryTodays2048')) ||
                button.textContent?.includes(t('try2048ForDate'))
              ) {
                dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                window.location.href = `/2048/${currentSeed}`;
              }
            }
          );

          // Return cleanup function
          return cleanup;
        }
      }, 0);
    }
  }, [showCompletionDialog, handleResetGame, t, currentSeed]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
      <div className="flex flex-col lg:flex-row gap-8">

        <div className="lg:flex-1">
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            {formatDisplayDate(currentPuzzleDate, t, i18n, '15puzzle', false, isPreviewMode)}
          </h1>
        </div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <div className="flex items-center justify-start flex-shrink overflow-hidden">
              {/* Difficulty selector with Reset button style */}
              <div className="flex items-center">
                <button
                  id="difficulty-button-15puzzle"
                  className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800 text-xs sm:text-base whitespace-nowrap"
                  onClick={() => toggleDifficultyDropdown(difficultyLevel, t, currentPuzzleDate, stats, isCompleted)}
                >
                  <Gauge className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{difficultyLevel === 1 ? t('easy') :
                         difficultyLevel === 2 ? t('medium') :
                         difficultyLevel === 4 ? t('expert') :
                         t('hard')}</span>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2 flex-shrink-0">
              <div className="text-base sm:text-lg md:text-xl font-mono text-gray-700 dark:text-gray-300">
                {formatTimeDisplay(elapsedTime)}
              </div>
              {!isCompleted && (
                <button
                  onClick={handlePauseToggle}
                  className={cn(
                    'p-2 rounded-lg min-w-11 min-h-11',
                    isPaused ? 'bg-lagoon text-white hover:bg-teal-800' : 'bg-marigold text-midnight hover:bg-yellow-600'
                  )}
                  aria-label={isPaused ? t('resume') : t('pause')}
                  title={isPaused ? t('resume') : t('pause')}
                >
                  {isPaused ? '▶' : '⏸'}
                </button>
              )}
            </div>
            <div className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center justify-end flex-shrink space-x-2">
              {/* Development only "Win" button - hide in preview mode */}
              {isDevelopment() && !isCompleted && !isPreviewMode && (
                <button
                  onClick={handleWin}
                  className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs sm:text-base whitespace-nowrap"
                >
                  <Trophy className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Win</span>
                </button>
              )}
              <button
                onClick={() => handleNewGame()}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800 text-xs sm:text-base whitespace-nowrap"
              >
                <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{t('reset')}</span>
              </button>
            </div>
          </div>

          {isPaused ? (
            <div className="relative aspect-square mb-4 max-w-md mx-auto">
              <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700 bg-opacity-90 dark:bg-opacity-90 rounded-lg">
                <div className="text-center">
                  <h2 className="text-xl font-bold mb-4">{t('gamePaused')}</h2>
                  <button
                    onClick={handlePauseToggle}
                    className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800"
                  >
                    {t('resume')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative max-w-md mx-auto">
              {/* Add hover area for completed game */}
              {isCompleted && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none overflow-visible"
                  aria-hidden="true"
                >
                  <div className={cn(
                    'w-full transition-opacity duration-300 rounded-lg flex items-center justify-center bg-gray-800',
                    isHoveringBoard ? 'opacity-100' : 'opacity-0',
                    'absolute'
                  )}
                  style={{
                    minHeight: '100%',
                    maxWidth: '100%',
                    overflow: 'visible'
                  }}
                  >
                    <img
                      src={puzzleImageWithExt}
                      alt={t('completedPuzzleImage')}
                      className="w-full"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        transition: 'all 0.5s ease-in-out',
                        position: 'relative'
                      }}
                    />
                  </div>
                </div>
              )}

              <div
                className={cn(
                  'grid grid-cols-4 gap-1 mb-4 aspect-square bg-gray-200 dark:bg-gray-700 p-1 rounded-lg select-none relative touch-none',
                  isCompleted ? 'cursor-pointer' : ''
                )}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onMouseEnter={isCompleted ? () => setIsHoveringBoard(true) : undefined}
                onMouseLeave={isCompleted ? () => setIsHoveringBoard(false) : undefined}
                onClickCapture={handleBoardClick}
                onClick={isCompleted ? (e) => {
                  // Prevent click event from bubbling up if already hovering
                  if (isHoveringBoard) {
                    e.stopPropagation();
                    setIsHoveringBoard(false);
                  } else {
                    setIsHoveringBoard(true);
                  }
                } : undefined}
              >
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                  </div>
                )}
                {isCompleted && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                    <div className={cn(
                      'text-center text-lg font-semibold bg-black bg-opacity-50 text-white px-4 py-2 rounded transition-opacity duration-300',
                      isHoveringBoard ? 'opacity-100' : 'opacity-0'
                    )}>
                      {t('viewFullImage')}
                    </div>
                  </div>
                )}
                {board.tiles.map((tile, index) => (
                  <div key={`position-${index}`} className="relative aspect-square">
                    {tile !== 0 && (
                      <button
                        onClick={() => handleTileClick(index)}
                        onDragStart={(event) => event.preventDefault()}
                        draggable={false}
                        disabled={tile === 0 || isCompleted || isPaused}
                        className={cn(
                          'absolute inset-0 rounded-md',
                          'hover:brightness-110',
                          'transition-transform duration-100 ease-in-out',
                          isCompleted ? (isHoveringBoard ? 'opacity-20' : 'opacity-75') : '',
                          correctlyPlacedTiles.some(tile => {
                            const tileRow = Math.floor(index / BOARD_SIZE);
                            const tileCol = index % BOARD_SIZE;
                            return tile.row === tileRow && tile.col === tileCol;
                          }) && 'bg-gradient-to-br from-amber-200 to-orange-200 dark:from-amber-700 dark:to-orange-700 animate-pulse shadow-lg',
                          'flex items-center justify-center' // Add flex layout to the button itself
                        )}
                        style={getTileStyle(index, tile)}
                        aria-label={t('tile', { number: tile })}
                        tabIndex={-1}
                      >
                        {/* The tile number is now in its own absolute-positioned container */}
                        {showNumbers && (
                          <div className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className={cn(
                              'bg-black bg-opacity-40 dark:bg-black dark:bg-opacity-60 text-white rounded-full flex items-center justify-center shadow-md',
                              'w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base md:text-lg font-bold',
                              'z-10' // Keep number above background
                            )}>
                              {tile}
                            </span>
                          </div>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center mt-4 gap-2">
            <button
              onClick={handleUndo}
              disabled={isPaused || isCompleted || moveHistory.length === 0}
              className={cn(
                'min-h-11 px-3 py-2 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed',
                'bg-cobalt hover:bg-blue-800'
              )}
              title={t('undoLastMove')}
            >
              {t('undo')}
            </button>

            {/* New Numbers button */}
            <button
              disabled={isPaused || isCompleted}
              className={cn(
                'min-h-11 px-3 py-2 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed',
                'bg-cobalt hover:bg-blue-800'
              )}
              onClick={() => {
                const newShowNumbers = !showNumbers;
                setShowNumbers(newShowNumbers);

                // If turning numbers on and they weren't used before, track it and show toast
                if (newShowNumbers && !stats.numbersUsed) {
                  dispatch({
                    type: 'UPDATE_STATS',
                    payload: { ...stats, numbersUsed: true }
                  });

                  // Show toast message
                  toast.info(t('toastNumbersUsed'), {
                    duration: 2000,
                    position: 'top-center'
                  });
                }
              }}
              title={showNumbers ? t('hideNumbers') : t('showNumbers')}
            >
              {showNumbers ? t('hideNumbers') : t('showNumbers')}
            </button>

            <button
              onClick={handleHint}
              disabled={isPaused || isCompleted}
              className={cn(
                'min-h-11 px-3 py-2 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed',
                hintRequested && calculatingSolution ? 'bg-marigold !text-midnight motion-safe:animate-pulse' : 'bg-marigold !text-midnight hover:bg-yellow-400'
              )}
              title={t('hintTooltipAutoMove')}
            >
              {hintRequested && calculatingSolution ? t('thinking') : t('hint')}
            </button>
          </div>
        </div>

        <aside className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed lg:w-80 space-y-8">
          <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{t('howToPlay')}</h2>
            <ul className="space-y-3 text-gray-600 dark:text-gray-300">
              {(t('15puzzle.instructions.points', { returnObjects: true }) as string[]).map((instruction, index) => (
                <li key={index}>{index + 1}. {instruction}</li>
              ))}
            </ul>
            <div className="mt-4 text-gray-600 dark:text-gray-300 font-medium">
              {t('15puzzle.instructions.goodLuck')}
            </div>

            {/* Collapsible keyboard controls section */}
            <div className="mt-4">
              <button
                onClick={() => setShowKeyboardControls(prev => !prev)}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center justify-between w-full text-left text-lg font-semibold text-gray-900 dark:text-white"
                aria-expanded={showKeyboardControls}
              >
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {showKeyboardControls ? '▼' : '►'} {t('keyboardControls')}
                </span>
              </button>

              {showKeyboardControls && (
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300 mt-1 pl-2">
                  <li><span className="font-semibold">↑↓←→</span>: {t('moveKeys')}</li>
                  <li><span className="font-semibold">{t('spaceKey')}</span>: {t('pauseResumeKey')}</li>
                  <li><span className="font-semibold">U</span>: {t('undoKey')}</li>
                  <li><span className="font-semibold">N</span>: {t('toggleNumbersKey')}</li>
                  <li><span className="font-semibold">H</span>: {t('hintKey')}</li>
                  <li><span className="font-semibold">K</span>: {t('toggleKeyboardControls')}</li>
                  <li><span className="font-semibold">Ctrl+R</span>: {t('resetKey')}</li>
                  <li><span className="font-semibold">Ctrl+D</span>: {t('changeDifficultyKey')}</li>
                </ul>
              )}
            </div>
          </div>
        </aside>
      </div>

      {showCompletionDialog && (
        <div role="dialog" aria-modal="true" aria-label={t('brand.completion')} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-md w-full mx-4 max-h-[90dvh] overflow-y-auto">
            <CompletionArt />
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-4 break-words text-center">
              {formatDisplayDate(currentPuzzleDate, t, i18n, '15puzzle', true)}
            </h2>

            <div className="space-y-3 mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                {t('time')}: {formatCompletionTime(elapsedTime)}
              </p>

              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{t('gameStatistics')}</h3>
              <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
                <li>{t('moves')}: {stats.moves}</li>
                {stats.hintsUsed == 1 && (
                  <li>{t('hintsUsed', { count: stats.hintsUsed })}</li>
                )}
                {stats.hintsUsed > 1 && (
                  <li>{t('hintsUsed_plural', { count: stats.hintsUsed })}</li>
                )}
                {stats.hintsUsed === 0 && (
                  <li>{t('noHintsUsed')}</li>
                )}
                {stats.numbersUsed && (
                  <li>{t('numbersUsed')}</li>
                )}
              </ul>

              {/* Day completion message */}
              {allGamesCompleted && (
                <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-2 border-yellow-300 dark:border-yellow-700">
                  <p className="text-yellow-700 dark:text-yellow-300 font-bold text-center text-lg mb-2">
                    {t('dayCompleted')} ⭐
                  </p>
                  <p className="text-yellow-600 dark:text-yellow-400 text-center">
                    {t('completedAllGames')}
                  </p>
                </div>
              )}

              {/* Challenge message based on performance */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <p className="text-blue-700 dark:text-blue-300 font-medium">
                  {stats.hintsUsed > 2 ? (
                    t('challengeFewerHints')
                  ) : stats.hintsUsed > 0 ? (
                    t('challengeNoHints')
                  ) : (stats.numbersUsed && difficultyLevel > 1) ? (
                    t('challengeNoNumbers')
                  ) : elapsedTime > 60 ? (
                    t('challengeFasterTime', {
                      additional: (difficultyLevel === 1 ? ' ' + t('orLevel', { level: t('medium') }) :
                         difficultyLevel === 2 ? ' ' + t('orLevel', { level: t('hard') }) :
                         difficultyLevel === 3 ? ' ' + t('orLevel', { level: t('expert') }) : '')
                    })
                  ) : (
                    t('challengeImpressivePerformance', {
                      additional: (difficultyLevel === 1 ? ' ' + t('canLevel', { level: t('medium') }) :
                         difficultyLevel === 2 ? ' ' + t('canLevel', { level: t('hard') }) :
                         difficultyLevel === 3 ? ' ' + t('canLevel', { level: t('expert') }) : '')
                    })
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {/* Add "View full image" button */}
              <button
                onClick={() => {
                  dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                  // Set isHoveringBoard to true to immediately show the full image
                  setIsHoveringBoard(true);
                }}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-lagoon text-white rounded hover:bg-teal-800"
              >
                {t('viewFullImage')}
              </button>

              {/* Only show "Try again" if performance was not impressive */}
              {(stats.hintsUsed > 0 || elapsedTime > 60 || stats.numbersUsed && difficultyLevel > 1) && (
                <button
                  onClick={() => {
                    dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                    handleResetGame();
                  }}
                  className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800"
                >
                  {t('tryAgain')}
                </button>
              )}

              {/* Show "Try today's 15 puzzle" option if the puzzle date is not today */}
              {(() => {
                const today = new Date();
                const isToday = currentPuzzleDate.getDate() === today.getDate() &&
                                currentPuzzleDate.getMonth() === today.getMonth() &&
                                currentPuzzleDate.getFullYear() === today.getFullYear();

                if (!isToday) {
                  return (
                    <button
                      onClick={() => {
                        dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                        // Navigate to today's 15 puzzle
                        window.location.href = '/15puzzle';
                      }}
                      className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded hover:bg-cobalt"
                    >
                      {t('tryTodays15Puzzle')}
                    </button>
                  );
                }
                return null;
              })()}

              {incompleteGames.includes('sudoku') && (
                <button
                  onClick={() => {
                    dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                    window.location.href = `/sudoku/${currentSeed}`;
                  }}
                  className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-lagoon text-white rounded hover:bg-teal-800"
                >
                  {(() => {
                    const today = new Date();
                    const isToday = currentPuzzleDate.getDate() === today.getDate() &&
                                    currentPuzzleDate.getMonth() === today.getMonth() &&
                                    currentPuzzleDate.getFullYear() === today.getFullYear();

                    return isToday ? t('tryTodaysSudoku') : t('trySudokuForDate', {
                      date: new Intl.DateTimeFormat(i18n.language || 'en', {
                        month: 'long',
                        day: 'numeric'
                      }).format(currentPuzzleDate)
                    });
                  })()}
                </button>
              )}

              {incompleteGames.includes('2048') && (
                <button
                  onClick={() => {
                    dispatch({ type: 'CLOSE_COMPLETION_DIALOG' });
                    window.location.href = `/2048/${currentSeed}`;
                  }}
                  className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-lagoon text-white rounded hover:bg-teal-800"
                >
                  {(() => {
                    const today = new Date();
                    const isToday = currentPuzzleDate.getDate() === today.getDate() &&
                                    currentPuzzleDate.getMonth() === today.getMonth() &&
                                    currentPuzzleDate.getFullYear() === today.getFullYear();

                    return isToday ? t('tryTodays2048') : t('try2048ForDate', {
                      date: new Intl.DateTimeFormat(i18n.language || 'en', {
                        month: 'long',
                        day: 'numeric'
                      }).format(currentPuzzleDate)
                    });
                  })()}
                </button>
              )}

              {/* Always show a close button */}
              <button
                onClick={() => dispatch({ type: 'CLOSE_COMPLETION_DIALOG' })}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmDialog && (
        <div role="dialog" aria-modal="true" aria-label={t('confirmReset')} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-sm w-full mx-4">
            <p className="text-gray-800 dark:text-gray-200 text-lg mb-4">
              {localStorage.getItem('fifteenPuzzleConfirmationType') === 'difficulty'
                ? t('confirmDifficultyChange')
                : t('confirmReset')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => dispatch({ type: 'CLOSE_CONFIRM_DIALOG' })}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleConfirmReset}
                className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800"
              >
                {t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
